import React, { useState, useEffect } from 'react';
import { getAllTemplates, getTemplate } from '../templates/templateDefinitions';
import { API_URLS } from '../config';
import './styles/TemplateSettings.css';

const TemplateSettings = ({ businessId, onSettingsChange }) => {
    const [selectedTemplate, setSelectedTemplate] = useState('classic');
    const [customTemplate, setCustomTemplate] = useState({
        backgroundColor: '#000000',
        backgroundType: 'solid',
        backgroundImage: null,
        textColor: '#ffffff',
        borderStyle: 'none',
        borderColor: '#ffffff',
        borderWidth: 2,
        surveyPosition: 'center',
        surveyMaxWidth: '600px',
        surveyBackground: 'transparent',
        surveyBorder: 'none',
        headerOverlay: false,
        customCSS: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const templates = getAllTemplates();

    const loadCurrentTemplate = async () => {
        if (!businessId) return;
        
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_URLS.template(businessId), {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data && data.template_config) {
                    const config = JSON.parse(data.template_config);
                    setSelectedTemplate(config.id || 'classic');
                    setCustomTemplate(config);
                }
            }
        } catch (error) {
            console.error('Error loading template:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadCurrentTemplate();
    }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplate(templateId);
        const template = getTemplate(templateId);
        setCustomTemplate(template);
    };

    const handleCustomChange = (field, value) => {
        setCustomTemplate(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBackgroundImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            handleCustomChange('backgroundImage', e.target.result);
            handleCustomChange('backgroundType', 'image');
        };
        reader.readAsDataURL(file);
    };

    const saveTemplate = async () => {
        if (!businessId) return;

        setIsLoading(true);
        setSaveStatus('');

        try {
            const token = localStorage.getItem('token');
            const templateConfig = {
                ...customTemplate,
                id: selectedTemplate,
                name: templates.find(t => t.id === selectedTemplate)?.name || 'Custom'
            };

            const response = await fetch(API_URLS.template(businessId), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    template_config: JSON.stringify(templateConfig)
                })
            });

            if (response.ok) {
                setSaveStatus('Template saved successfully!');
                onSettingsChange?.();
            } else {
                setSaveStatus('Error saving template');
            }
        } catch (error) {
            console.error('Error saving template:', error);
            setSaveStatus('Error saving template');
        } finally {
            setIsLoading(false);
        }
    };

    const getPreviewStyle = (template) => {
        const style = {
            backgroundColor: template.backgroundColor,
            color: template.textColor,
            backgroundImage: template.backgroundImage ? `url(${template.backgroundImage})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
        };

        if (template.backgroundType === 'gradient') {
            style.background = template.backgroundColor;
        }

        return style;
    };

    return (
        <div className="template-settings">
            <div className="template-section">
                <h3>Choose a Template</h3>
                <div className="template-grid">
                    {templates.map(template => (
                        <div 
                            key={template.id}
                            className={`template-option ${selectedTemplate === template.id ? 'selected' : ''}`}
                            onClick={() => handleTemplateSelect(template.id)}
                        >
                            <div 
                                className="template-preview"
                                style={getPreviewStyle(template)}
                            >
                                <div className="preview-header"></div>
                                <div className="preview-survey">
                                    <div className="preview-text">Survey</div>
                                    <div className="preview-stars">★★★★★</div>
                                </div>
                            </div>
                            <div className="template-info">
                                <h4>{template.name}</h4>
                                <p>{template.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="customization-section">
                <h3>Customize Selected Template</h3>
                
                {/* Background Settings */}
                <div className="setting-group">
                    <label>Background Type</label>
                    <select 
                        value={customTemplate.backgroundType} 
                        onChange={(e) => handleCustomChange('backgroundType', e.target.value)}
                    >
                        <option value="solid">Solid Color</option>
                        <option value="gradient">Gradient</option>
                        <option value="image">Image</option>
                    </select>
                </div>

                {customTemplate.backgroundType === 'solid' && (
                    <div className="setting-group">
                        <label>Background Color</label>
                        <input 
                            type="color" 
                            value={customTemplate.backgroundColor}
                            onChange={(e) => handleCustomChange('backgroundColor', e.target.value)}
                        />
                    </div>
                )}

                {customTemplate.backgroundType === 'gradient' && (
                    <div className="setting-group">
                        <label>Gradient CSS</label>
                        <input 
                            type="text"
                            value={customTemplate.backgroundColor}
                            onChange={(e) => handleCustomChange('backgroundColor', e.target.value)}
                            placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        />
                    </div>
                )}

                {customTemplate.backgroundType === 'image' && (
                    <div className="setting-group">
                        <label>Background Image</label>
                        <input 
                            type="file"
                            accept="image/*"
                            onChange={handleBackgroundImageUpload}
                        />
                    </div>
                )}

                {/* Text Color */}
                <div className="setting-group">
                    <label>Text Color</label>
                    <input 
                        type="color" 
                        value={customTemplate.textColor}
                        onChange={(e) => handleCustomChange('textColor', e.target.value)}
                    />
                </div>

                {/* Survey Position */}
                <div className="setting-group">
                    <label>Survey Position</label>
                    <select 
                        value={customTemplate.surveyPosition} 
                        onChange={(e) => handleCustomChange('surveyPosition', e.target.value)}
                    >
                        <option value="center">Center</option>
                        <option value="overlay">Overlay on Header</option>
                        <option value="bottom">Bottom</option>
                    </select>
                </div>

                {/* Survey Width */}
                <div className="setting-group">
                    <label>Survey Max Width</label>
                    <input 
                        type="text"
                        value={customTemplate.surveyMaxWidth}
                        onChange={(e) => handleCustomChange('surveyMaxWidth', e.target.value)}
                        placeholder="600px"
                    />
                </div>

                {/* Survey Background */}
                <div className="setting-group">
                    <label>Survey Background</label>
                    <input 
                        type="text"
                        value={customTemplate.surveyBackground}
                        onChange={(e) => handleCustomChange('surveyBackground', e.target.value)}
                        placeholder="transparent or rgba(0,0,0,0.7)"
                    />
                </div>

                {/* Border Style */}
                <div className="setting-group">
                    <label>Border Style</label>
                    <select 
                        value={customTemplate.borderStyle} 
                        onChange={(e) => handleCustomChange('borderStyle', e.target.value)}
                    >
                        <option value="none">None</option>
                        <option value="solid">Solid</option>
                        <option value="rounded">Rounded</option>
                        <option value="glass">Glass Effect</option>
                    </select>
                </div>

                {customTemplate.borderStyle !== 'none' && (
                    <>
                        <div className="setting-group">
                            <label>Border Color</label>
                            <input 
                                type="color" 
                                value={customTemplate.borderColor}
                                onChange={(e) => handleCustomChange('borderColor', e.target.value)}
                            />
                        </div>
                        <div className="setting-group">
                            <label>Border Width</label>
                            <input 
                                type="number"
                                value={customTemplate.borderWidth}
                                onChange={(e) => handleCustomChange('borderWidth', parseInt(e.target.value))}
                                min="0"
                                max="10"
                            />
                        </div>
                    </>
                )}

                {/* Advanced Settings */}
                <div className="advanced-toggle">
                    <button 
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                        {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
                    </button>
                </div>

                {showAdvanced && (
                    <div className="advanced-settings">
                        <div className="setting-group">
                            <label>Custom CSS</label>
                            <textarea 
                                value={customTemplate.customCSS}
                                onChange={(e) => handleCustomChange('customCSS', e.target.value)}
                                placeholder="Add custom CSS here..."
                                rows="6"
                            />
                        </div>
                    </div>
                )}

                <div className="save-section">
                    <button 
                        type="button" 
                        onClick={saveTemplate}
                        disabled={isLoading}
                        className="save-button"
                    >
                        {isLoading ? 'Saving...' : 'Save Template'}
                    </button>
                    {saveStatus && (
                        <div className={`save-status ${saveStatus.includes('Error') ? 'error' : 'success'}`}>
                            {saveStatus}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplateSettings;
